// frontend/src/pages/client/DashboardClient.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BanknotesIcon,
  ShoppingCartIcon,
  ChartBarSquareIcon,
  CubeIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  PlusIcon,
  ArrowUpIcon,
  ExclamationCircleIcon,
  CalendarIcon,
  ChartBarIcon,
  ChartPieIcon,
  ArrowRightIcon,
  BuildingStorefrontIcon,
  CpuChipIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/solid';
import {
  AreaChart, Area,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';
import API from '../../services/api';
import './DashboardClient.css';

/* ── Animation variants ── */
const fadeUp = {
  hidden:  { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 280, damping: 24 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

/* ── Build 7-day chart data from transactions ── */
const buildAreaData = (txList) => {
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    return {
      label: d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
      date:  d.toDateString(),
    };
  });
  return days.map(({ label, date }) => {
    const dayTx = txList.filter(t => new Date(t.created_at).toDateString() === date);
    return {
      date:  label,
      achats:  dayTx.filter(t => t.transaction_type === 'purchase').reduce((s, t) => s + (t.points_amount || 0), 0),
      consommations: dayTx.filter(t => t.transaction_type !== 'purchase').reduce((s, t) => s + (t.points_amount || 0), 0),
    };
  });
};

/* ── AI Assistant bot responses ── */
const BOT_RESPONSES = {
  solde:        (stats) => `Votre solde actuel est de **${Number(stats.total_points).toLocaleString()} points**. Vous avez rechargé ${Number(stats.points_purchased).toLocaleString()} pts et consommé ${Number(stats.points_used).toLocaleString()} pts.`,
  acheter:      ()      => `Pour acheter des points, rendez-vous dans la section **Wallet**. Plusieurs packs sont disponibles selon vos besoins.`,
  transactions: (stats, txList) => txList.length === 0
    ? `Vous n'avez aucune transaction pour le moment.`
    : `Vos ${txList.length} dernière(s) transaction(s) sont visibles dans la section **Historique**.`,
  services:     (stats) => `Vous avez **${stats.services_count} service${stats.services_count > 1 ? 's' : ''}** actif${stats.services_count > 1 ? 's' : ''}. Explorez la section **Services** pour en découvrir d'autres.`,
  aide:         ()      => `Pour toute assistance, contactez notre support à **support@pointpay.com** ou consultez la section **Paramètres**.`,
  default:      ()      => `Je suis votre assistant POINTPAY. Je peux vous aider avec votre solde, vos transactions, l'achat de points et vos services. Comment puis-je vous aider ?`,
};
const getBotReply = (input, stats, txList) => {
  const q = input.toLowerCase();
  if (q.includes('solde') || q.includes('points') || q.includes('balance'))
    return BOT_RESPONSES.solde(stats);
  if (q.includes('acheter') || q.includes('achat') || q.includes('buy') || q.includes('recharger'))
    return BOT_RESPONSES.acheter();
  if (q.includes('transaction') || q.includes('historique') || q.includes('paiement'))
    return BOT_RESPONSES.transactions(stats, txList);
  if (q.includes('service') || q.includes('actif'))
    return BOT_RESPONSES.services(stats);
  if (q.includes('aide') || q.includes('help') || q.includes('support'))
    return BOT_RESPONSES.aide();
  return BOT_RESPONSES.default();
};

const SUGGESTION_ICONS = {
  'fa-coins':     <BanknotesIcon style={{ width: 18, height: 18, flexShrink: 0 }} />,
  'fa-history':   <ChartBarSquareIcon style={{ width: 18, height: 18, flexShrink: 0 }} />,
  'fa-cart-plus': <ShoppingCartIcon style={{ width: 18, height: 18, flexShrink: 0 }} />,
  'fa-cubes':     <CubeIcon style={{ width: 18, height: 18, flexShrink: 0 }} />,
};

const SUGGESTIONS = [
  { label: 'Mon solde',            icon: 'fa-coins'      },
  { label: 'Voir mes transactions',icon: 'fa-history'    },
  { label: 'Acheter des points',   icon: 'fa-cart-plus'  },
  { label: 'Services actifs',      icon: 'fa-cubes'      },
];

const PIE_COLORS = ['#2563eb', '#06b6d4', '#5822d6', '#044777', '#f59e0b'];

/* ─────────────────────────────────────────────── */

const DashboardClient = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const timerRef  = useRef(null);
  const chatEndRef = useRef(null);

  const [user, setUser]               = useState(null);
  const [wallets, setWallets]         = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats]             = useState({ total_points: 0, services_count: 0, points_used: 0, points_purchased: 0 });
  const [loading, setLoading]         = useState(true);
  const [msg, setMsg]                 = useState(null);
  const [activeTab, setActiveTab]     = useState('my-services');

  /* Chat state */
  const [chatInput, setChatInput]     = useState('');
  const [chatMessages, setChatMessages] = useState([
    { from: 'bot', text: 'Bonjour ! Je suis votre assistant POINTPAY. Comment puis-je vous aider aujourd\'hui ?' }
  ]);
  const [botTyping, setBotTyping]     = useState(false);

  /* Stripe success message */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('success') === '1') {
      setMsg({ type: 'success', text: 'Points crédités sur votre compte !' });
      timerRef.current = setTimeout(() => setMsg(null), 5000);
    }
    return () => clearTimeout(timerRef.current);
  }, [location]);

  /* Load user + data */
  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) setUser(JSON.parse(u));
    fetchData();
  }, []);

  /* Auto-scroll chat */
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, botTyping]);

  const fetchData = async () => {
    try {
      const [walletsRes, txRes, statsRes, servicesRes] = await Promise.all([
        API.get('/client/wallets'),
        API.get('/client/transactions?limit=20'),
        API.get('/client/stats'),
        API.get('/client/services/all')
      ]);
      setWallets(walletsRes.data.wallets || []);
      setTransactions(txRes.data.transactions || []);
      setStats(statsRes.data.stats || {});
      setAvailableServices(servicesRes.data.services || []);
    } catch (err) {
      console.error('Erreur dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const usageRate = stats.points_purchased > 0
    ? Math.round((stats.points_used / stats.points_purchased) * 100) : 0;

  /* ── Chat send ── */
  const sendMessage = useCallback((text) => {
    const input = (text || chatInput).trim();
    if (!input) return;
    setChatInput('');
    setChatMessages(m => [...m, { from: 'user', text: input }]);
    setBotTyping(true);
    setTimeout(() => {
      const reply = getBotReply(input, stats, transactions);
      setChatMessages(m => [...m, { from: 'bot', text: reply }]);
      setBotTyping(false);
    }, 900 + Math.random() * 600);
  }, [chatInput, stats, transactions]);

  /* ── Chart data ── */
  const areaData = buildAreaData(transactions);
  const pieData  = wallets.length > 0
    ? wallets.map(w => ({ name: w.service_name, value: Number(w.balance) }))
    : [{ name: 'Aucun service', value: 1 }];

  const barData = [
    { label: 'Rechargé', value: Number(stats.points_purchased) || 0, fill: '#2563eb' },
    { label: 'Consommé', value: Number(stats.points_used)      || 0, fill: '#06b6d4' },
    { label: 'Restant',  value: Number(stats.total_points)     || 0, fill: '#3803b3' },
  ];

  /* ── KPIs ── */
  const kpiIconStyle = { width: 18, height: 18, flexShrink: 0 };
  const kpis = [
    {
      label: 'Solde disponible',
      value: Number(stats.total_points).toLocaleString(),
      unit: 'pts',
      icon: <BanknotesIcon style={kpiIconStyle} />,
      color: '#2563eb',
      bg: '#eff6ff',
      trend: stats.total_points > 0 ? '+actif' : '—',
      trendOk: stats.total_points > 0,
    },
    {
      label: 'Total rechargé',
      value: Number(stats.points_purchased).toLocaleString(),
      unit: 'pts',
      icon: <ShoppingCartIcon style={kpiIconStyle} />,
      color: '#10b981',
      bg: '#f0fdf4',
      trend: 'Cumulé',
      trendOk: true,
    },
    {
      label: 'Points consommés',
      value: Number(stats.points_used).toLocaleString(),
      unit: 'pts',
      icon: <ChartBarSquareIcon style={kpiIconStyle} />,
      color: '#f59e0b',
      bg: '#fffbeb',
      trend: `${usageRate}% utilisé`,
      trendOk: usageRate < 80,
    },
    {
      label: 'Services actifs',
      value: stats.services_count,
      unit: '',
      icon: <CubeIcon style={kpiIconStyle} />,
      color: '#8b5cf6',
      bg: '#f5f3ff',
      trend: stats.services_count > 0 ? `${stats.services_count} wallet${stats.services_count > 1 ? 's' : ''}` : 'Aucun',
      trendOk: stats.services_count > 0,
    },
    {
      label: 'Disponibilité',
      value: '99.9',
      unit: '%',
      icon: <CpuChipIcon style={kpiIconStyle} />,
      color: '#06b6d4',
      bg: '#ecfeff',
      trend: 'Système OK',
      trendOk: true,
    },
  ];

  if (loading) return (
    <div className="dc-loading">
      <div className="dc-spinner" />
      <p>Chargement de votre espace...</p>
    </div>
  );

  const today = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  /* ── Tooltip custom recharts ── */
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <div className="dc-chart-tooltip">
          <p className="dc-ct-label">{label}</p>
          {payload.map((p, i) => (
            <p key={i} style={{ color: p.color }}>
              {p.name}: <strong>{p.value.toLocaleString()} pts</strong>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  /* ═══════════════════════════════════════════════ */
  return (
    <div className="dc-page">

      {/* ── Alert ── */}
      <AnimatePresence>
        {msg && (
          <motion.div
            className={`dc-alert dc-alert--${msg.type}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <CheckCircleIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> {msg.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ════════════════════════════════════════
          PROFILE CARD
      ════════════════════════════════════════ */}
      <motion.div
        className="dc-profile-card"
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 220, damping: 22 }}
      >
        {/* Décorations */}
        <div className="dc-pc-orb dc-pc-orb-1" />
        <div className="dc-pc-orb dc-pc-orb-2" />

        <div className="dc-pc-left">
          <div className="dc-pc-avatar">
            {user?.first_name?.[0]?.toUpperCase() || 'C'}
          </div>
          <div className="dc-pc-info">
            <div className="dc-pc-greeting">Bonjour,</div>
            <h1 className="dc-pc-name">{user?.first_name} {user?.last_name}</h1>
            <div className="dc-pc-meta">
              <span className="dc-pc-tag"><EnvelopeIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> {user?.email}</span>
              <span className="dc-pc-status active"><span style={{ width: 7, height: 7, borderRadius: '50%', background: 'currentColor', display: 'inline-block', flexShrink: 0 }} /> Compte actif</span>
            </div>
          </div>
        </div>

        <div className="dc-pc-right">
          <div className="dc-pc-balance-box">
            <span className="dc-pcb-label"><BanknotesIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> Solde total</span>
            <span className="dc-pcb-value">{Number(stats.total_points).toLocaleString()}</span>
            <span className="dc-pcb-unit">points disponibles</span>
          </div>
          <div className="dc-pc-actions">
            <motion.button
              className="dc-pc-btn dc-pc-btn--primary"
              onClick={() => navigate('/dashboard/client/buy')}
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.96 }}
            >
              <PlusIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> Recharger
            </motion.button>
            <motion.button
              className="dc-pc-btn dc-pc-btn--ghost"
              onClick={() => navigate('/dashboard/client/transactions')}
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.96 }}
            >
              <ChartBarSquareIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> Historique
            </motion.button>
          </div>
          <div className="dc-pc-date">
            <CalendarIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> {today}
          </div>
        </div>
      </motion.div>

      {/* ════════════════════════════════════════
          KPI STRIP
      ════════════════════════════════════════ */}
      <motion.div
        className="dc-kpi-strip"
        variants={stagger}
        initial="hidden"
        animate="visible"
      >
        {kpis.map(k => (
          <motion.div
            key={k.label}
            className="dc-kpi"
            variants={fadeUp}
            whileHover={{ y: -4, boxShadow: '0 16px 32px rgba(15,23,42,0.12)' }}
          >
            <div className="dc-kpi-icon-wrap" style={{ background: k.bg, color: k.color }}>
              {k.icon}
            </div>
            <div className="dc-kpi-body">
              <span className="dc-kpi-label">{k.label}</span>
              <div className="dc-kpi-value-row">
                <span className="dc-kpi-value">{k.value}</span>
                {k.unit && <span className="dc-kpi-unit">{k.unit}</span>}
              </div>
              <span className={`dc-kpi-trend ${k.trendOk ? 'ok' : 'warn'}`}>
                {k.trendOk
                  ? <ArrowUpIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
                  : <ExclamationCircleIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
                }
                {k.trend}
              </span>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* ════════════════════════════════════════
          MAIN GRID  (charts + AI panel)
      ════════════════════════════════════════ */}
      <div className="dc-grid">

        {/* ── Left: charts ── */}
        <div className="dc-charts-col">

          {/* Area chart */}
          <motion.div
            className="dc-card"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="dc-card-header">
              <div>
                <h3><ChartBarIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> Activité des points (7 jours)</h3>
                <p>Achats et consommations journaliers</p>
              </div>
              <button className="dc-card-action" onClick={() => navigate('/dashboard/client/transactions')}>
                Voir tout <ArrowRightIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
              </button>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={areaData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gAchats" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#2563eb" stopOpacity={0.25}/>
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="gConso" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#06b6d4" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="achats"        name="Achats"        stroke="#2563eb" strokeWidth={2.5} fill="url(#gAchats)" dot={false} activeDot={{ r: 5 }} />
                <Area type="monotone" dataKey="consommations" name="Consommations"  stroke="#06b6d4" strokeWidth={2.5} fill="url(#gConso)"  dot={false} activeDot={{ r: 5 }} />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Bottom row: Bar + Pie */}
          <div className="dc-charts-row">

            {/* Bar chart */}
            <motion.div
              className="dc-card"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="dc-card-header">
                <div>
                  <h3><ChartBarIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> Répartition des points</h3>
                  <p>Vue globale rechargé / consommé / restant</p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={barData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    formatter={(v) => [`${v.toLocaleString()} pts`, '']}
                    contentStyle={{ borderRadius: 10, border: '1px solid #e4ebf5', fontSize: 12 }}
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {barData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </motion.div>

            {/* Pie chart */}
            <motion.div
              className="dc-card"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
            >
              <div className="dc-card-header">
                <div>
                  <h3><ChartPieIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> Services</h3>
                  <p>Répartition par wallet</p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%" cy="50%"
                    innerRadius={44}
                    outerRadius={68}
                    paddingAngle={3}
                    stroke="none"
                  >
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v, n) => [`${v.toLocaleString()} pts`, n]}
                    contentStyle={{ borderRadius: 10, border: '1px solid #e4ebf5', fontSize: 12 }}
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </motion.div>

          </div>

          {/* ── Services / Tabs ── */}
          <motion.div
            className="dc-card"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="dc-card-header">
              <div className="dc-tabs">
                <button className={`dc-tab ${activeTab === 'my-services' ? 'active' : ''}`} onClick={() => setActiveTab('my-services')}>
                  <CubeIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> Mes services
                </button>
                <button className={`dc-tab ${activeTab === 'available' ? 'active' : ''}`} onClick={() => setActiveTab('available')}>
                  <BuildingStorefrontIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> Disponibles
                </button>
              </div>
              <button className="dc-card-action" onClick={() => navigate('/dashboard/client/services')}>
                Explorer <ArrowRightIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
              </button>
            </div>

            <AnimatePresence mode="wait">
              {activeTab === 'my-services' && (
                <motion.div
                  key="mine"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {wallets.length === 0 ? (
                    <div className="dc-empty">
                      <CubeIcon className="dc-empty-icon" style={{ width: 18, height: 18, flexShrink: 0 }} />
                      <p>Aucun service actif. Explorez les services disponibles.</p>
                      <button className="dc-btn-sm" onClick={() => setActiveTab('available')}>Explorer</button>
                    </div>
                  ) : (
                    <div className="dc-services-grid">
                      {wallets.map((w, i) => (
                        <motion.div
                          key={w.id}
                          className="dc-service-card"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.06 }}
                        >
                          <div className="dc-svc-icon"><CubeIcon style={{ width: 18, height: 18, flexShrink: 0 }} /></div>
                          <div className="dc-svc-body">
                            <span className="dc-svc-name">{w.service_name}</span>
                            <span className="dc-svc-pts">{Number(w.balance).toLocaleString()} pts</span>
                          </div>
                          <div className="dc-svc-bar-bg">
                            <motion.div
                              className="dc-svc-bar"
                              initial={{ width: 0 }}
                              animate={{ width: `${Math.min(100, (w.balance / 1000) * 100)}%` }}
                              transition={{ duration: 0.9, delay: 0.2 + i * 0.07 }}
                            />
                          </div>
                          <button className="dc-btn-sm" onClick={() => navigate(`/dashboard/client/buy?service=${w.company_id}`)}>
                            <PlusIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> Recharger
                          </button>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {activeTab === 'available' && (
                <motion.div
                  key="avail"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {availableServices.length === 0 ? (
                    <div className="dc-empty">
                      <BuildingStorefrontIcon className="dc-empty-icon" style={{ width: 18, height: 18, flexShrink: 0 }} />
                      <p>Aucun service disponible pour le moment.</p>
                    </div>
                  ) : (
                    <div className="dc-services-grid">
                      {availableServices.map((svc) => (
                        <div key={svc.id} className="dc-service-card">
                          <div className="dc-svc-icon" style={{ background: '#f0fdf4', color: '#10b981' }}>
                            <BuildingStorefrontIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
                          </div>
                          <div className="dc-svc-body">
                            <span className="dc-svc-name">{svc.name}</span>
                            <span className="dc-svc-pts">{svc.partner_name}</span>
                          </div>
                          <button className="dc-btn-sm" onClick={() => navigate(`/dashboard/client/buy?service=${svc.id}`)}>
                            <ShoppingCartIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> Voir
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

        </div>

        {/* ── Right: AI Assistant ── */}
        <motion.div
          className="dc-ai-panel"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.25, type: 'spring', stiffness: 220, damping: 24 }}
        >
          {/* Panel header */}
          <div className="dc-ai-header">
            <div className="dc-ai-avatar">
              <CpuChipIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
            </div>
            <div>
              <div className="dc-ai-title">Assistant IA</div>
              <div className="dc-ai-status"><span className="dc-ai-dot"></span> En ligne</div>
            </div>
            <div className="dc-ai-badge">ProxyPay AI</div>
          </div>

          {/* Messages */}
          <div className="dc-ai-messages">
            {chatMessages.map((m, i) => (
              <motion.div
                key={i}
                className={`dc-msg dc-msg--${m.from}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                {m.from === 'bot' && (
                  <div className="dc-msg-bot-icon"><CpuChipIcon style={{ width: 18, height: 18, flexShrink: 0 }} /></div>
                )}
                <div className="dc-msg-bubble">
                  {m.text.split('**').map((part, pi) =>
                    pi % 2 === 1
                      ? <strong key={pi}>{part}</strong>
                      : <span key={pi}>{part}</span>
                  )}
                </div>
              </motion.div>
            ))}
            {botTyping && (
              <div className="dc-msg dc-msg--bot">
                <div className="dc-msg-bot-icon"><CpuChipIcon style={{ width: 18, height: 18, flexShrink: 0 }} /></div>
                <div className="dc-msg-bubble dc-msg-typing">
                  <span /><span /><span />
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Suggestions */}
          <div className="dc-ai-suggestions">
            {SUGGESTIONS.map(s => (
              <button
                key={s.label}
                className="dc-ai-chip"
                onClick={() => sendMessage(s.label)}
              >
                {SUGGESTION_ICONS[s.icon]} {s.label}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="dc-ai-input-row">
            <input
              className="dc-ai-input"
              placeholder="Posez votre question..."
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendMessage()}
            />
            <motion.button
              className="dc-ai-send"
              onClick={() => sendMessage()}
              disabled={!chatInput.trim()}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.92 }}
            >
              <PaperAirplaneIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
            </motion.button>
          </div>

        </motion.div>

      </div>

      {/* ════════════════════════════════════════
          TRANSACTIONS TABLE
      ════════════════════════════════════════ */}
      <motion.div
        className="dc-card"
        style={{ marginTop: 0 }}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
      >
        <div className="dc-card-header">
          <div>
            <h3><ChartBarSquareIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> Transactions récentes</h3>
            <p>Dernières opérations sur votre compte</p>
          </div>
          <button className="dc-card-action" onClick={() => navigate('/dashboard/client/transactions')}>
            Voir tout <ArrowRightIcon style={{ width: 18, height: 18, flexShrink: 0 }} />
          </button>
        </div>

        {transactions.slice(0, 5).length === 0 ? (
          <div className="dc-empty">
            <ChartBarSquareIcon className="dc-empty-icon" style={{ width: 18, height: 18, flexShrink: 0 }} />
            <p>Aucune transaction pour le moment</p>
          </div>
        ) : (
          <div className="dc-table-wrap">
            <table className="dc-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Service</th>
                  <th>Type</th>
                  <th>Montant</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {transactions.slice(0, 5).map((tx, i) => (
                  <motion.tr
                    key={tx.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.46 + i * 0.05 }}
                  >
                    <td className="tc-date">
                      {new Date(tx.created_at).toLocaleDateString('fr-FR')}
                      <span className="tc-time">{new Date(tx.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                    </td>
                    <td>
                      <div className="dc-tx-service">
                        <div className="dc-tx-icon"><CubeIcon style={{ width: 18, height: 18, flexShrink: 0 }} /></div>
                        {tx.service_name || '—'}
                      </div>
                    </td>
                    <td>
                      <span className={`dc-badge ${tx.transaction_type === 'purchase' ? 'dc-badge--buy' : 'dc-badge--use'}`}>
                        {tx.transaction_type === 'purchase' ? 'Achat' : 'Consommation'}
                      </span>
                    </td>
                    <td className={tx.transaction_type === 'purchase' ? 'dc-pos' : 'dc-neg'}>
                      {tx.transaction_type === 'purchase' ? '+' : '-'}{tx.points_amount} pts
                    </td>
                    <td>
                      <span className="dc-badge dc-badge--ok">
                        <CheckCircleIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> Réussi
                      </span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

    </div>
  );
};

export default DashboardClient;
